import { type GenericMap } from "@common/declarations";
import { isArray, isNull, isUndefined, pickBy, trimStart } from "lodash";
import moment, { isMoment } from "moment";

const parseString = <T = GenericMap<string>>(
  s: string,
  separator: string,
  fn?: (value: string) => string
): Partial<T> => {
  return s.split(separator).reduce((res, keyValue) => {
    const parts = keyValue.split("=");
    return {
      ...res,
      [parts[0]]: fn ? fn(parts[1]) : parts[1]
    };
  }, {});
};

export const serializeQueryString = (qs: object): string => {
  const keys = Object.keys(qs);
  if (keys.length) {
    return (
      "?" +
      keys
        .filter((key) => !isUndefined(qs[key]) && !isNull(qs[key]))
        .map((key) => `${key}=${encodeURIComponent(qs[key])}`)
        .join("&")
    );
  }
  return "";
};

export const addQueryString = (url: string, qs: object): string => {
  const eq = parseQueryString(url);
  return replaceQueryString(url, { ...(eq || {}), ...qs });
};

export const cleanQueryStringParameters = (qs: GenericMap<string>): GenericMap<string> =>
  pickBy(qs, (x) => !(isUndefined(x) || isNull(x) || x === ""));

export const replaceQueryString = (url: string, qs: object): string => {
  const urlWOQs = url.indexOf("?") >= 0 ? url.substr(0, url.indexOf("?")) : url;
  return `${urlWOQs}${serializeQueryString(qs)}`;
};

export const upsertQueryString = (url: string, qs: object): string => {
  const existingQs = parseQueryString(url);
  return replaceQueryString(url, { ...existingQs, ...qs });
};

export const parseQueryString = <T = GenericMap<any>>(url?: string): Partial<T> => {
  url = url || window.location.href;
  if (!url || !url.includes("?")) return {};

  const match = url.match(/\?([^#]+)/);
  if (!match) return {};

  return parseString(match[1], "&", decodeURIComponent);
};

const mapQSValue = (value: any): string | null => {
  if (isNull(value) || isUndefined(value)) return null;

  if (isMoment(value)) {
    return (value as moment.Moment).toDate().toISOString();
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === "object") {
    return encodeURIComponent(JSON.stringify(value));
  }

  return encodeURIComponent(value);
};

const mapQSKeyValue = (key: string, value: any): string | null => {
  return `${key}=${mapQSValue(value) ?? ""}`;
};

export const toQueryString = (obj: any, arrayMode: "json" | "repeat" = "repeat"): string => {
  return Object.keys(cleanQueryStringParameters(obj))
    .reduce((res, k) => {
      // NOTE: array values should be expanded: e.g. foo: [1,2] => ?foo=1&foo=2
      if (isArray(obj[k]) && arrayMode === "repeat") {
        return [...res, ...obj[k].map((x) => mapQSKeyValue(k, x))];
      }

      return [...res, mapQSKeyValue(k, obj[k])];
    }, [])
    .join("&");
};

export const slugUrl = (url: string | undefined | null): string =>
  (url || "")
    .toLowerCase()
    .replace(/[^a-zA-Z\d\s:]/g, "")
    .trim()
    .replace(/  +/g, " ")
    .replace(/ +/g, "-");

export const updateQueryString = (url: string, qs: object): string => {
  const hash = url.match(/#.+$/);
  const urlWOHash = hash ? url.replace(/#.+$/, "") : url;
  const urlWOQs = urlWOHash.indexOf("?") >= 0 ? urlWOHash.substr(0, urlWOHash.indexOf("?")) : urlWOHash;
  const query = toQueryString(qs);
  return `${urlWOQs}${query ? `?${query}` : ""}${hash ?? ""}`;
};

export const combineUrl = (...parts: string[]): string => {
  const url = parts
    .filter((x) => x)
    .map((x) => trimStart(x, "/"))
    .join("/");
  return url.startsWith("http") ? url : `/${url}`;
};

export const getIsIframed = (): boolean => !!parseQueryString(window.location.href).iframe;

export const getHostName = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};
